import type {
  AttachedStatementReference,
  StatementReferenceHelpRequest,
} from './extractedStatements';

/**
 * The expert's "Find references" search, as the chat holds it.
 *
 * The backend tracks each request from the triage wizard as a
 * `citation_request` row driven by a Temporal workflow:
 *
 *   queued → searching → awaiting_review → published
 *                                       ↘ failed
 *                                       ↘ cancelled
 *
 * The expert reviews the proposed candidates on `awaiting_review`,
 * approves a subset, and the workflow publishes the approved set onto
 * `attachedStatementReferences` on the parent Twilio message.
 *
 * These types mirror the generated OpenAPI schemas field for field. The
 * adapter returns wire values as these types and passes these types as
 * request bodies, so a contract change that drifts from them fails to
 * compile there rather than at runtime.
 *
 * The backend announces each status change on the realtime channel, and the
 * feature's realtime handler re-reads the citation-request query.
 */
export type CitationRequestStatus =
  | 'queued'
  | 'searching'
  | 'awaiting_review'
  | 'published'
  | 'failed'
  | 'cancelled';

export type ProposedReference = {
  id: string;
  citationRequestId: string;
  statementIndex: number;
  url: string;
  title: string | null;
  journal: string | null;
  authors: string[];
  year: number | null;
  snippet: string | null;
  score: number | null;
  source: string;
  createdAt: string;
};

export type CitationRequest = {
  id: string;
  inquiryId: number;
  conversationId: string;
  messageSid: string;
  conversationMessageId: string;
  requesterUserId: number;
  statementIndices: number[];
  status: CitationRequestStatus;
  notifyWhenReady: boolean;
  /**
   * Stamped when the expert explicitly asks for, or adopts, the search.
   * `null` means an automatic search the expert has never opened.
   */
  requestedAt: string | null;
  errorMessage: string | null;
  workflowId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  proposedReferences: ProposedReference[];
};

export type CreateCitationRequestInput = {
  conversationId: string;
  messageSid: string;
  statementIndices: number[];
};

export type CitationRequestPublishDiff = {
  attachedStatementReferences: {
    added: AttachedStatementReference[];
    removed: AttachedStatementReference[];
  };
  statementReferenceHelpRequests: {
    added: StatementReferenceHelpRequest[];
    updated: StatementReferenceHelpRequest[];
    removed: StatementReferenceHelpRequest[];
  };
};

/**
 * `freeTextReferences` carries every `(statementIndex, url)` currently
 * attached to the message that did NOT originate from this citation
 * request (manual-paste URLs plus refs from prior requests on the same
 * message) so the backend preserves them through the reconcile.
 * `helpRequests` is the target state for `statementReferenceHelpRequests`
 * scoped to the request's statement indices: an empty `description`
 * removes an entry, a non-empty one upserts it.
 */
export type ApproveCitationRequestInput = {
  citationRequestId: string;
  approvedReferenceIds: string[];
  freeTextReferences: { statementIndex: number; url: string }[];
  helpRequests: { statementIndex: number; description: string }[];
};

export interface ApproveCitationRequestResult {
  request: CitationRequest;
  diff: CitationRequestPublishDiff;
}

export interface CancelCitationRequestInput {
  citationRequestId: string;
}

/**
 * Opt-in (or clear) for "notify me when ready" in the searching phase.
 * The backend reads `notifyWhenReady` once, when the request reaches
 * `awaiting_review`, and sends the references-ready notification then.
 */
export type NotifyCitationRequestInput = {
  citationRequestId: string;
  notifyWhenReady: boolean;
};

/**
 * Discriminator for `CitationRequestCreateError`. `persist_failed`
 * covers any server-side or transport failure on create. Kept as a
 * union so future create-failure modes have a typed home.
 */
export type CitationRequestCreateErrorKind = 'persist_failed';

/**
 * Thrown when a request cannot be started. Carries a discriminator so the
 * triage wizard can report a typed failure kind to analytics without
 * parsing API error strings.
 */
export class CitationRequestCreateError extends Error {
  constructor(
    public readonly kind: CitationRequestCreateErrorKind,
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);

    this.name = 'CitationRequestCreateError';
  }
}

/**
 * In-flight statuses — the expert can't open a new triage request while
 * one is running.
 */
export const IN_FLIGHT_CITATION_STATUSES: readonly CitationRequestStatus[] = [
  'queued',
  'searching',
  'awaiting_review',
];

/** True for statuses where the workflow hasn't terminally completed. */
export function isInFlightStatus(status: CitationRequestStatus): boolean {
  return IN_FLIGHT_CITATION_STATUSES.includes(status);
}

/**
 * Statuses where the request is still actionable from the chat
 * surface — either in-flight, OR `published`. Publish is a
 * non-destructive sync, so a published request keeps driving the pill
 * and the review sheet to preserve the expert's approval set across
 * republish.
 *
 * `failed` and `cancelled` are the only statuses that drop a request
 * off the chat surface entirely.
 */
export const REVIEWABLE_CITATION_STATUSES: readonly CitationRequestStatus[] = [
  ...IN_FLIGHT_CITATION_STATUSES,
  'published',
];

export function isReviewableStatus(status: CitationRequestStatus): boolean {
  return REVIEWABLE_CITATION_STATUSES.includes(status);
}

/**
 * Terminal statuses where the request has *unexpectedly* ended — the
 * workflow never got the expert's approval set onto the message and
 * the local review state no longer applies. Distinguished from
 * `published`, which also terminates the workflow on the server but
 * leaves the request reviewable.
 *
 * Co-located with the reviewable list so a status added in the future
 * has one obvious place to be categorised.
 */
export const UNEXPECTED_TERMINAL_CITATION_STATUSES: readonly CitationRequestStatus[] = [
  'failed',
  'cancelled',
];

export function isUnexpectedTerminalStatus(status: CitationRequestStatus): boolean {
  return UNEXPECTED_TERMINAL_CITATION_STATUSES.includes(status);
}

/**
 * Pick the most recent reviewable request for a given message. Many
 * historical requests can exist per message (cancel-and-restart); the
 * UI only ever cares about the latest one whose state is still
 * actionable from the chat bubble.
 *
 * Returns `undefined` if no request matches the message or if every
 * matching request has terminated as `failed` / `cancelled`. Published
 * requests stay surfaced — the expert can keep refining their
 * approval set and republishing against the same request.
 */
export function pickActiveCitationRequest(
  requests: readonly CitationRequest[] | undefined,
  messageSid: string
): CitationRequest | undefined {
  if (!requests) {
    return undefined;
  }

  const candidates = requests.filter(
    (request) => request.messageSid === messageSid && isReviewableStatus(request.status)
  );

  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.reduce((latest, request) =>
    request.updatedAt > latest.updatedAt ? request : latest
  );
}

/**
 * Merge a freshly created or adopted request into a conversation's list so
 * the parent bubble flips to its workflow state on the next render.
 *
 * If an entry with the same `id` is already there (a realtime invalidation
 * raced the POST response), replace it in place to preserve list order;
 * otherwise append. An empty or absent list is seeded with just the new
 * request, and the refetch that follows reconciles it.
 */
export function mergeCitationRequest(
  previous: CitationRequest[] | undefined,
  next: CitationRequest
): CitationRequest[] {
  if (!previous || previous.length === 0) {
    return [next];
  }

  const existingIndex = previous.findIndex((entry) => entry.id === next.id);

  if (existingIndex === -1) {
    return [...previous, next];
  }

  const copy = [...previous];

  copy[existingIndex] = next;

  return copy;
}
